"use client";

// The compound root: `<RelayHistory>` + `.Trigger` + `.Content` — the
// standalone transfer-history modal. Task 12's simpler sibling of
// `RelayDeposit.tsx` (Task 11): same wiring shape (every prop goes into a
// context so `.Content`, supplied by the host as a child, can read them; the
// shell knobs go into `ResponsiveShell`), but with no wizard state of its
// own -- `<RelayHistory.Content>` is just `useRelayOrders` + `<OrdersList>`.
import { RelayHistoryContext } from "../providers/relay-history-provider";
import type { RelayHistoryProps } from "../types";
import { RelayHistoryContent } from "./RelayHistoryContent";
import { RelayHistoryTrigger } from "./RelayHistoryTrigger";
import { ResponsiveShell } from "./shell/ResponsiveShell";
import "../styles/component.css";

function RelayHistoryRoot(props: RelayHistoryProps) {
  const {
    theme = "dark",
    mobileBreakpoint,
    drawerDirection,
    // The history table needs more horizontal room than the deposit form.
    // Keep the shell overrideable, but make the standalone history modal wide
    // enough to show all six desktop columns without horizontal scrolling.
    dialogWidth = "min(640px, calc(100vw - 32px))",
    drawerMaxWidth,
    drawerMinWidth,
    style,
    open,
    onOpenChange,
    children,
  } = props;

  return (
    // `props` is a fresh object each render, so consumers re-render exactly when
    // this root does -- same rationale as RelayDepositRoot.
    <RelayHistoryContext.Provider value={props}>
      <ResponsiveShell
        theme={theme}
        mobileBreakpoint={mobileBreakpoint}
        drawerDirection={drawerDirection}
        dialogWidth={dialogWidth}
        drawerMaxWidth={drawerMaxWidth}
        drawerMinWidth={drawerMinWidth}
        style={style}
        open={open}
        onOpenChange={onOpenChange}
      >
        {children}
      </ResponsiveShell>
    </RelayHistoryContext.Provider>
  );
}

export const RelayHistory = Object.assign(RelayHistoryRoot, {
  Trigger: RelayHistoryTrigger,
  Content: RelayHistoryContent,
});
